import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import {
	Actor,
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	IAnonymousStudentService
} from '@studyportals/anonymous-student-interfaces';
import { IStudent, StudentField } from '@studyportals/studentdomain';
import docCookies from '../../../JS/Vendor/cookies';
import { currencyGetFromCountry } from '@studyportals/static-domain-data/currencies/currency-get-from-country';
import trackingDataHandler from './../../../JS/Tracking/TrackingDataHandler';

/* eslint-disable
	@typescript-eslint/no-misused-promises,
	@typescript-eslint/no-unsafe-assignment,
	@typescript-eslint/no-unsafe-member-access,
	@typescript-eslint/no-unsafe-call,
	@typescript-eslint/no-unsafe-argument,
	@typescript-eslint/no-non-null-assertion
 */
export class UserSyncService implements ISubscriber<AnonymousStudentServiceReady | AnonymousStudentProfileUpdated> {
	private anonymousStudent!: IAnonymousStudentService;

	constructor() {
		window.EventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, this, true);
		window.EventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, this, false);
	}

	public async notify(event: AnonymousStudentServiceReady | AnonymousStudentProfileUpdated): Promise<void> {
		if (event.eventType === AnonymousStudentServiceReady.EventType) {
			await this.handleAnonymousStudentServiceReady(event as AnonymousStudentServiceReady);
		}
		if (event.eventType === AnonymousStudentProfileUpdated.EventType) {
			this.handleAnonymousStudentProfileUpdated(event as AnonymousStudentProfileUpdated);
		}
	}

	private async handleAnonymousStudentServiceReady(event: AnonymousStudentServiceReady): Promise<void> {
		this.anonymousStudent = event.anonymousStudentService;
		await this.syncLocalizedUserData();

		document.addEventListener('trackingready', async () => this.syncLocalizedUserData());
	}

	private handleAnonymousStudentProfileUpdated(event: AnonymousStudentProfileUpdated): void {
		const changes = event.changes;
		if (changes[StudentField.NATIONALITY_COUNTRY_ID] || changes[StudentField.NATIONALITY_COUNTRY_ISO]) {
			this.setNationalityCookies(
				changes[StudentField.NATIONALITY_COUNTRY_ID],
				changes[StudentField.NATIONALITY_COUNTRY_ISO]
			);
		}
	}

	private async syncLocalizedUserData(): Promise<void> {
		const data = await this.anonymousStudent.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ISO,
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.CURRENCY
		]);

		if (
			data[StudentField.NATIONALITY_COUNTRY_ID] === undefined &&
			data[StudentField.NATIONALITY_COUNTRY_ISO] === undefined
		) {
			await this.setNationality();
		} else {
			if (this.shouldLogMissingNationalityIsoOrId(data)) {
				window.Rollbar.warn('UserSyncService: nationality country iso or id not set', data);
			}

			this.setNationalityCookies(
				data[StudentField.NATIONALITY_COUNTRY_ID],
				data[StudentField.NATIONALITY_COUNTRY_ISO]
			);
		}

		if (data[StudentField.CURRENCY] === undefined) {
			await this.setCurrency(data[StudentField.NATIONALITY_COUNTRY_ISO]);
		}
	}

	private async setNationality(): Promise<void> {
		const data = this.getTrackingData();
		if (data === null || data.origin_iso === null) {
			return;
		}

		const iso = data.origin_iso.toUpperCase();
		const id = data.origin;

		await this.anonymousStudent.setStudentData(
			{
				[StudentField.NATIONALITY_COUNTRY_ID]: id,
				[StudentField.NATIONALITY_COUNTRY_ISO]: iso
			},
			Actor.AUTOMATION
		);

		this.setNationalityCookies(id, iso);
	}

	private async setCurrency(nationality_country_iso?: string): Promise<void> {
		const trackData = this.getTrackingData();

		let currency = null;
		if (nationality_country_iso) {
			currency = currencyGetFromCountry(nationality_country_iso);
		} else if (trackData !== null && trackData.origin_iso !== null) {
			currency = currencyGetFromCountry(trackData.origin_iso.toUpperCase());
		}

		if (currency === null || currency === undefined) {
			return;
		}

		await this.anonymousStudent.setStudentData({ [StudentField.CURRENCY]: currency?.iso }, Actor.AUTOMATION);
	}

	private setNationalityCookies(id?: number, iso?: string): void {
		if (id) {
			this.setCookie('user_nationality_id', id);
		}
		if (iso) {
			this.setCookie('user_nationality_iso', iso);
		}
	}

	private setCookie(key: string, value: string | number): void {
		docCookies.setItem(key, value, 31536000, '/');
	}

	private getTrackingData(): any {
		return trackingDataHandler.retrieveTrackingDetails();
	}

	private shouldLogMissingNationalityIsoOrId(data: IStudent): boolean {
		return (
			window.Rollbar !== undefined &&
			(data[StudentField.NATIONALITY_COUNTRY_ID] === undefined ||
				data[StudentField.NATIONALITY_COUNTRY_ISO] === undefined)
		);
	}
}
